import { useTranslation } from "react-i18next";

import { colors } from "@dotnet/telematics-design-system";

import { ProgressBarMultiple } from "components/DesignComponents/ProgressBar/ProgressBarMultiple";
import { useFormattedNumber } from "shared/hooks/useFormattedNumber";

import { getTotalCount } from "../../../functions";
import { useStatusColor } from "../../../hooks";
import { TASK_OVERVIEW_STATUSES, TaskEntry } from "../../../types";
import TooltipContent from "./TooltipContent";
import { SingleProgressBarProps } from "./types";

const SingleProgressBar = ({
    label,
    data,
    isLoading,
    hoveredPart,
    handleHoverOn,
    handleHoverOff
}: SingleProgressBarProps) => {
    const { textDisabled, white } = colors;

    const { t } = useTranslation();
    const { getStatusColor } = useStatusColor();
    const { renderFormattedNumber } = useFormattedNumber();

    const getHoverColor = (id: string) =>
        hoveredPart !== null && hoveredPart !== id
            ? "rgba(255, 255, 255, 0.75)"
            : undefined;

    const stats = Object.entries(data).map(([status, taskEntries]) => {
        const id = `${label}-${status}`;
        const value = getTotalCount(taskEntries);

        return {
            id,
            value,
            color: getStatusColor(status as TASK_OVERVIEW_STATUSES),
            hoverColor: getHoverColor(id),
            tooltipContent: (
                <TooltipContent
                    status={status as TASK_OVERVIEW_STATUSES}
                    timeframe={label}
                    taskEntries={taskEntries}
                />
            )
        };
    });

    const total: number = Object.values(data).reduce(
        (sum: number, taskEntries: TaskEntry[]) =>
            sum + getTotalCount(taskEntries),
        0
    );

    return (
        <ProgressBarMultiple
            id={label}
            titleLeft={t(`Dashboard##tasks##${label}##progressBarTitle`)}
            titleRight={isLoading ? "" : renderFormattedNumber(total)}
            stats={stats}
            titleColor={
                hoveredPart !== null && !hoveredPart.includes(label)
                    ? textDisabled
                    : undefined
            }
            isLoading={isLoading}
            emptyStatsHoverColor={getHoverColor(label)}
            tooltipContentForEmptyStats={
                <TooltipContent
                    status={TASK_OVERVIEW_STATUSES.NoTasks}
                    timeframe={label}
                    taskEntries={[]}
                />
            }
            tooltipProps={{
                slotProps: {
                    tooltip: {
                        sx: {
                            background: white,
                            borderRadius: "8px",
                            padding: "8px 16px",
                            marginBottom: "8px !important",
                            boxShadow:
                                "0px 0px 2px 0px rgba(37, 42, 49, 0.16), 0px 1px 4px 0px rgba(37, 42, 49, 0.12)"
                        }
                    }
                }
            }}
            onHoverOn={handleHoverOn}
            onHoverOff={handleHoverOff}
        />
    );
};

export default SingleProgressBar;
